<template>
  <div class="h-full overflow-x-auto">
    <loader :is-loading="loading" />
    <lf-table
      v-model:pin-enabled="pinEnabled"
      :columns="columns"
      :data="data"
      :loading="loading"
      :select-all="select"
      class="scorecard-list-table"
      :class="tableHeightClass"
      :checked-rows="selectedIds"
      row-is-clickable
      pin-option
      header-sticky
      :freeze-first-two-columns-when-pinned="isWorkflowBuilder"
      @row-clicked="editScorecardGroup"
      @selected-rows="handleSelectAll"
      @selected-row="handleSelectRow"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import compact from "lodash/compact";

import { formatUserFullName } from "@/helpers/formatting";
import { scorecardResultsRunAtFormatter } from "@/helpers/scorecards";
import {
  ScorecardsIsBacktestReport,
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { useLocalStorageSetting } from "@/hooks/options";
import { useScorecards } from "@/hooks/scorecards";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";

import ScorecardActionMenu from "@/components/scorecards/ScorecardActionMenu.vue";
import ScorecardFavoriteButton from "@/components/scorecards/ScorecardFavoriteButton.vue";
import ScorecardId from "@/components/scorecards/tableComponents/ScorecardId.vue";
import ScorecardKnockout from "@/components/scorecards/tableComponents/ScorecardKnockout.vue";
import ScorecardNameRunAndEdit from "@/components/scorecards/tableComponents/ScorecardNameRunAndEdit.vue";
import ScorecardPoints from "@/components/scorecards/tableComponents/ScorecardPoints.vue";
import ScorecardPointsAndResultsBar from "@/components/scorecards/tableComponents/ScorecardPointsAndResultsBar.vue";
import ScorecardRiskTier from "@/components/scorecards/tableComponents/ScorecardRiskTier.vue";
import ScorecardStatus from "@/components/scorecards/tableComponents/ScorecardStatus.vue";
import LfTable from "@/components/ui/table/LfTable.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";
import TemplateStatusView from "@/views/profile/components/TemplateStatusView.vue";

import type { IDealsColumn } from "@/models/applications";
import type { IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  data: {
    type: Array as PropType<IScorecardGroup[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  select: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emit = defineEmits<{
  (eventName: "update:selectedIds", newSelectedIds: string[]): void;
}>();

const pageIsView = inject(ScorecardsIsViewKey, true);
const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder, false);
const isBacktestReport = inject(ScorecardsIsBacktestReport, false);

const tableHeightClass = computed(() =>
  pageIsView ? "h-[75vh] 2xl:h-[80vh]" : "h-[55vh] 2xl:h-[65vh]"
);

const { getters } = useStore();

const { canUseScorecardActions } = useScorecards(false);
const router = useRouter();
const { canEditWorkflowTemplate } = useActiveWorkflowTemplate();
const { t } = useI18n();
const pinEnabled = useLocalStorageSetting(
  "scorecardGroupListTablePinEnabled",
  false
);

const isViewOrWorkflowBuilder = pageIsView || isWorkflowBuilder;
const isWorkflowLive = !isViewOrWorkflowBuilder;

const columns = computed(() =>
  compact<IDealsColumn>([
    {
      label: t("COMMON.TEMPLATE_NAME"),
      key: "name",
      component: ScorecardNameRunAndEdit,
      columnClass: "w-[400px]",
      props: {
        pageIsView,
        isWorkflowLive,
        isScorecardGroup: true,
      },
    },
    isViewOrWorkflowBuilder
      ? { label: t("COMMON.DESCRIPTION"), key: "description" }
      : null,
    {
      label: t("COMMON.TEMPLATE_ID"),
      key: "id",
      component: ScorecardId,
    },
    {
      label: t("COMMON.STATUS"),
      key: "status",
      component: TemplateStatusView,
      props: {
        statuses: statuses.value,
      },
    },
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.KNOCKOUT"),
          key: "knockout",
          component: ScorecardKnockout,
        }
      : null,
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.STATUS"),
          key: "status",
          component: ScorecardStatus,
        }
      : null,
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.RISK_TIER"),
          key: "risk_tier",
          component: ScorecardRiskTier,
        }
      : null,
    isWorkflowLive && {
      label: t("SCORECARDS.RUN_AT"),
      key: "results",
      component: DateAndTimeDisplay,
      formatter: scorecardResultsRunAtFormatter,
    },
    !isWorkflowBuilder && !pageIsView
      ? {
          label: "",
          key: "points-bar",
          component: ScorecardPointsAndResultsBar,
          columnClass: "min-w-[300px] points-bar",
          props: {
            pageIsView,
            isWorkflowLive,
            isScorecardGroup: true,
          },
        }
      : null,
    {
      label: t("SCORECARDS.PTS"),
      key: "points",
      component: ScorecardPoints,
      columnClass: "w-[90px]",
      props: { pageIsView, isWorkflowBuilder },
    },
    !isWorkflowLive && !isBacktestReport
      ? {
          label: t("COMMON.UPDATED_BY"),
          key: "latest_activity.updated_by",
          formatter: (value) => formatUserFullName(value),
        }
      : null,
    !isWorkflowLive && !isBacktestReport
      ? {
          label: t("COMMON.UPDATED_AT"),
          key: "latest_activity.updated_at",
          component: DateAndTimeDisplay,
        }
      : null,
    showEditMenu.value
      ? {
          label: "",
          key: "actions",
          component: ScorecardActionMenu,
          props: { isGroup: true },
        }
      : null,
    isWorkflowBuilder || isWorkflowLive
      ? {
          label: "",
          key: "favorite",
          component: ScorecardFavoriteButton,
          columnClass: "w-[70px]",
        }
      : null,
  ])
);

const showEditMenu = computed(
  () =>
    !isWorkflowBuilder &&
    !isWorkflowLive &&
    !isBacktestReport &&
    pageIsView &&
    canUseScorecardActions.value
);

const statuses = computed(() => getters["options/dataOrchestrationStatuses"]);

const editScorecardGroup = (id: string) => {
  if (isWorkflowBuilder && !canEditWorkflowTemplate.value) {
    return;
  }
  const scorecardGroup = props.data.find((sc) => sc.id === id);
  if (!scorecardGroup) {
    return;
  }
  if (pageIsView && !props.select && !isBacktestReport) {
    router.push({
      name: "ScorecardsGroupEdit",
      params: {
        id,
      },
    });
    return;
  }
  if (isBacktestReport) {
    emit("update:selectedIds", [id]);
  }
};

const handleSelectAll = (rows: Array<string>, checked?: boolean) => {
  if (typeof checked === "undefined") {
    return;
  }
  emit("update:selectedIds", rows);
};

const handleSelectRow = (id: string | number, checked: boolean) => {
  const stringId = String(id);
  const filtered = props.selectedIds.filter(
    (selectedId) => selectedId !== stringId
  );
  const payload = checked ? [...filtered, stringId] : filtered;
  emit("update:selectedIds", payload);
};
</script>
